"use client";
import { createContext, useContext } from "react";
import type { CashflowSummary, Currency } from "@/types";
import type { ProjectionScenario } from "@/constants/projectionScenarioSelect";

export type { Currency };

interface CashflowContextValue {
  summary: CashflowSummary | undefined;
  isLoading: boolean;
  error: unknown;
  effectiveConsolidated: boolean;
  dateFrom: string;
  dateTo: string;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  currency: Currency;
  setCurrency: (v: Currency) => void;
  projectionScenario: ProjectionScenario;
  setProjectionScenario: (v: ProjectionScenario) => void;
}

export const CashflowContext = createContext<CashflowContextValue | null>(null);

export function useCashflowContext() {
  const ctx = useContext(CashflowContext);
  if (!ctx)
    throw new Error("useCashflowContext must be used within CashflowProvider");
  return ctx;
}

export function formatCurrency(amount: number, currency: Currency): string {
  return new Intl.NumberFormat(currency === "ILS" ? "he-IL" : "en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatK(value: number, currency: Currency): string {
  const symbol = currency === "ILS" ? "₪" : "$";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1000) {
    return `${sign}${symbol}${(abs / 1000).toFixed(1)}K`;
  }
  return `${sign}${symbol}${abs.toFixed(0)}`;
}
